import type { paths } from '@cou/contracts';
import { adminApi, type ApiFailure } from '../api/admin-api';

/*
 * The Admins API client (API-25, admin-ui.md → Admins). SUPER_ADMIN-only account management: list
 * operator accounts, create one (the response carries a one-time temporary password), and apply the
 * PUT subset (role / active state / forced reset). Injectable at the screen for tests, mirroring
 * apps/admin/src/players/players-client.ts.
 */

export type AdminAccount =
  paths['/api/admin/admins']['get']['responses'][200]['content']['application/json']['admins'][number];
export type AdminAccountCreated =
  paths['/api/admin/admins']['post']['responses'][200]['content']['application/json'];
export type UpdateAdminPatch = NonNullable<
  paths['/api/admin/admins/{id}']['put']['requestBody']
>['content']['application/json'];

type ListAdminsBody =
  paths['/api/admin/admins']['get']['responses'][200]['content']['application/json'];

type RoleMatrixBody =
  paths['/api/admin/roles']['get']['responses'][200]['content']['application/json'];
/** One seeded role with its granted permission ids — a column of the "Role permissions" card. */
export type AdminRole = RoleMatrixBody['roles'][number];
/** One permission of the catalogue (id + description) — a row of the "Role permissions" card. */
export type AdminPermission = RoleMatrixBody['permissions'][number];

export type ListAdminsResult = ({ ok: true } & ListAdminsBody) | ApiFailure;
export type ListRolesResult = ({ ok: true } & RoleMatrixBody) | ApiFailure;
export type CreateAdminResult = { ok: true; admin: AdminAccountCreated } | ApiFailure;
export type UpdateAdminResult = { ok: true; admin: AdminAccount } | ApiFailure;

/** The create form's inputs (API-25 POST body). */
export interface CreateAdminInput {
  readonly email: string;
  readonly role: string;
}

/** List every admin account with role and active state (API-25 GET). */
export async function listAdmins(adminToken: string): Promise<ListAdminsResult> {
  return adminApi.get<ListAdminsBody>(adminToken, '/api/admin/admins');
}

/**
 * The seeded RBAC role→permission matrix and permission catalogue, for the "Role permissions"
 * reference card (CAE-483). Reads the same DB tables login mints token permissions from, so the card
 * always shows the grants a role actually gets — no hand-kept copy to drift.
 */
export async function listRoles(adminToken: string): Promise<ListRolesResult> {
  return adminApi.get<RoleMatrixBody>(adminToken, '/api/admin/roles');
}

/** Create an admin account; the result carries the one-time temporary password (API-25 POST). */
export async function createAdmin(
  adminToken: string,
  input: CreateAdminInput,
): Promise<CreateAdminResult> {
  const result = await adminApi.post<AdminAccountCreated>(adminToken, '/api/admin/admins', input);
  return result.ok ? { ok: true, admin: result.body } : result;
}

/** Change an admin's role, active state, or forced-reset flag — any subset (API-25 PUT). */
export async function updateAdmin(
  adminToken: string,
  id: string,
  patch: UpdateAdminPatch,
): Promise<UpdateAdminResult> {
  const result = await adminApi.put<AdminAccount>(adminToken, `/api/admin/admins/${id}`, patch);
  return result.ok ? { ok: true, admin: result.body } : result;
}

export interface AdminsClient {
  listAdmins(adminToken: string): Promise<ListAdminsResult>;
  listRoles(adminToken: string): Promise<ListRolesResult>;
  createAdmin(adminToken: string, input: CreateAdminInput): Promise<CreateAdminResult>;
  updateAdmin(adminToken: string, id: string, patch: UpdateAdminPatch): Promise<UpdateAdminResult>;
}

export const defaultAdminsClient: AdminsClient = {
  listAdmins,
  listRoles,
  createAdmin,
  updateAdmin,
};
